import {
  Directive,
  ElementRef,
  Renderer2,
  input,
  output,
  OnInit,
  OnDestroy,
  effect,
  model,
  inject,
} from '@angular/core';

@Directive({
  selector: '[uiImageUpload]',
  standalone: true,
})
export class ImageUploadDirective implements OnInit, OnDestroy {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);

  file = model<File | null>(null);
  imageUrl = input<string | null>(null);
  accept = model<string>('image/*');
  showControls = input<boolean>(true);
  overlayStyles = input<Record<string, string>>({});
  minHeight = input<string>('40px');
  placeholderText = input<string>('');

  fileChanged = output<File | null>();

  private fileInput: HTMLInputElement | null = null;
  private overlay: HTMLElement | null = null;
  private uploadBtn: HTMLElement | null = null;
  private removeBtn: HTMLElement | null = null;
  private placeholderOverlay: HTMLElement | null = null;
  private currentObjectUrl: string | null = null;

  constructor() {
    effect(() => {
      const file = this.file();
      const imageUrl = this.imageUrl();
      if (file) {
        this.updateImagePreview(file);
        this.toggleControls(true);
      } else if (imageUrl) {
        this.updateImagePreviewFromUrl(imageUrl);
        this.toggleControls(true);
      } else {
        this.clearImagePreview();
        this.toggleControls(false);
      }
    });
  }

  ngOnInit() {
    this.setupFileInput();
    if (this.showControls()) {
      this.setupPlaceholder();
      this.setupOverlay();
    }
    this.setupDragAndDrop();
    this.ensureMinHeight();
  }

  ngOnDestroy() {
    this.cleanup();
  }

  private ensureMinHeight() {
    const hostElement = this.el.nativeElement;
    const currentHeight = hostElement.offsetHeight;
    const minHeightValue = parseInt(this.minHeight());

    if (currentHeight < minHeightValue) {
      this.renderer.setStyle(hostElement, 'minHeight', this.minHeight());
    }
  }

  private setupFileInput() {
    this.fileInput = this.renderer.createElement('input');
    this.renderer.setAttribute(this.fileInput, 'type', 'file');
    this.renderer.setAttribute(this.fileInput, 'accept', this.accept());
    this.renderer.setStyle(this.fileInput, 'display', 'none');

    this.renderer.listen(this.fileInput, 'change', (event) => {
      this.onFileChange(event);
    });

    this.renderer.appendChild(document.body, this.fileInput);
  }

  private setupPlaceholder() {
    const hostElement = this.el.nativeElement;

    this.placeholderOverlay = this.renderer.createElement('div');
    this.renderer.addClass(this.placeholderOverlay, 'image-upload-placeholder');
    this.renderer.addClass(this.placeholderOverlay, 'bg-light');
    this.renderer.addClass(this.placeholderOverlay, 'text-muted');
    this.renderer.setStyle(this.placeholderOverlay, 'position', 'absolute');
    this.renderer.setStyle(this.placeholderOverlay, 'top', '0');
    this.renderer.setStyle(this.placeholderOverlay, 'left', '0');
    this.renderer.setStyle(this.placeholderOverlay, 'right', '0');
    this.renderer.setStyle(this.placeholderOverlay, 'bottom', '0');
    this.renderer.setStyle(this.placeholderOverlay, 'display', 'flex');
    this.renderer.setStyle(this.placeholderOverlay, 'alignItems', 'center');
    this.renderer.setStyle(this.placeholderOverlay, 'justifyContent', 'center');
    this.renderer.setStyle(this.placeholderOverlay, 'flexDirection', 'column');
    this.renderer.setStyle(this.placeholderOverlay, 'gap', '8px');
    this.renderer.setStyle(this.placeholderOverlay, 'zIndex', '5');
    this.renderer.setStyle(this.placeholderOverlay, 'cursor', 'pointer');
    this.renderer.setStyle(
      this.placeholderOverlay,
      'transition',
      'all 0.3s ease',
    );

    const height = hostElement.offsetHeight;
    const isSmall = height < 100;

    this.renderer.setProperty(
      this.placeholderOverlay,
      'innerHTML',
      isSmall
        ? `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16" style="opacity: 0.5;">
        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
      </svg>
    `
        : `
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16" style="opacity: 0.5;">
        <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
        <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z"/>
      </svg>
      <span style="opacity: 0.75; font-size: 14px;">${this.placeholderText()}</span>
    `,
    );

    this.renderer.listen(this.placeholderOverlay, 'click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.openFileDialog();
    });

    this.renderer.listen(this.placeholderOverlay, 'mouseenter', () => {
      this.renderer.addClass(this.placeholderOverlay, 'bg-secondary');
      this.renderer.removeClass(this.placeholderOverlay, 'bg-light');

      if (this.overlay && !this.hasImage()) {
        this.renderer.setStyle(this.overlay, 'display', 'flex');
      }
    });

    this.renderer.listen(this.placeholderOverlay, 'mouseleave', () => {
      this.renderer.addClass(this.placeholderOverlay, 'bg-light');
      this.renderer.removeClass(this.placeholderOverlay, 'bg-secondary');

      if (this.overlay && !this.hasImage()) {
        this.renderer.setStyle(this.overlay, 'display', 'none');
      }
    });

    this.renderer.appendChild(hostElement, this.placeholderOverlay);
  }

  private setupOverlay() {
    const hostElement = this.el.nativeElement;

    const position = window.getComputedStyle(hostElement).position;
    if (position === 'static') {
      this.renderer.setStyle(hostElement, 'position', 'relative');
    }

    this.overlay = this.renderer.createElement('div');
    this.renderer.addClass(this.overlay, 'image-upload-overlay');

    const defaultStyles = {
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      display: 'none',
      background: 'rgba(0, 0, 0, 0.5)',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      zIndex: '10',
      transition: 'opacity 0.2s',
      pointerEvents: 'none',
    };

    const customStyles = this.overlayStyles();
    const finalStyles = { ...defaultStyles, ...customStyles };

    Object.entries(finalStyles).forEach(([key, value]) => {
      this.renderer.setStyle(this.overlay, key, value);
    });

    this.uploadBtn = this.renderer.createElement('button');
    this.renderer.addClass(this.uploadBtn, 'btn');
    this.renderer.addClass(this.uploadBtn, 'btn-sm');
    this.renderer.addClass(this.uploadBtn, 'btn-primary');
    this.renderer.addClass(this.uploadBtn, 'pointer-events-none');
    this.renderer.setAttribute(this.uploadBtn, 'type', 'button');
    this.renderer.setStyle(this.uploadBtn, 'pointerEvents', 'auto');
    this.renderer.setProperty(
      this.uploadBtn,
      'innerHTML',
      `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
      </svg>
    `,
    );

    this.renderer.listen(this.uploadBtn, 'click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.openFileDialog();
    });

    this.removeBtn = this.renderer.createElement('button');
    this.renderer.addClass(this.removeBtn, 'btn');
    this.renderer.addClass(this.removeBtn, 'btn-sm');
    this.renderer.addClass(this.removeBtn, 'btn-danger');
    this.renderer.setAttribute(this.removeBtn, 'type', 'button');
    this.renderer.setStyle(this.removeBtn, 'display', 'none');
    this.renderer.setStyle(this.removeBtn, 'pointerEvents', 'auto');
    this.renderer.setProperty(
      this.removeBtn,
      'innerHTML',
      `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
      </svg>
    `,
    );

    this.renderer.listen(this.removeBtn, 'click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.removeImage();
    });

    this.renderer.appendChild(this.overlay, this.uploadBtn);
    this.renderer.appendChild(this.overlay, this.removeBtn);
    this.renderer.appendChild(hostElement, this.overlay);

    this.renderer.listen(hostElement, 'mouseenter', () => {
      if (this.overlay && this.hasImage()) {
        this.renderer.setStyle(this.overlay, 'display', 'flex');
      }
    });

    this.renderer.listen(hostElement, 'mouseleave', () => {
      if (this.overlay && this.hasImage()) {
        this.renderer.setStyle(this.overlay, 'display', 'none');
      }
    });
  }

  private hasImage(): boolean {
    return !!this.file() || !!this.imageUrl();
  }

  private setupDragAndDrop() {
    const hostElement = this.el.nativeElement;

    this.renderer.listen(hostElement, 'dragover', (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      this.renderer.addClass(hostElement, 'drag-over');
    });

    this.renderer.listen(hostElement, 'dragleave', (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      this.renderer.removeClass(hostElement, 'drag-over');
    });

    this.renderer.listen(hostElement, 'drop', (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      this.renderer.removeClass(hostElement, 'drag-over');
      this.onDrop(event);
    });
  }

  private onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (file.type.startsWith('image/')) {
        this.fileChanged.emit(file);
      }
    }
    input.value = '';
  }

  private onDrop(event: DragEvent) {
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        this.fileChanged.emit(file);
      }
    }
  }

  private updateImagePreview(file: File) {
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
    }

    this.currentObjectUrl = URL.createObjectURL(file);

    const imgElement = this.el.nativeElement.querySelector('img');
    if (imgElement) {
      this.renderer.setAttribute(imgElement, 'src', this.currentObjectUrl);
      this.renderer.setStyle(imgElement, 'display', 'block');
    }
  }

  private updateImagePreviewFromUrl(url: string) {
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
      this.currentObjectUrl = null;
    }

    const imgElement = this.el.nativeElement.querySelector('img');
    if (imgElement) {
      this.renderer.setAttribute(imgElement, 'src', url);
      this.renderer.setStyle(imgElement, 'display', 'block');
    }
  }

  private clearImagePreview() {
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
      this.currentObjectUrl = null;
    }

    const imgElement = this.el.nativeElement.querySelector('img');
    if (imgElement) {
      this.renderer.removeAttribute(imgElement, 'src');
      this.renderer.setStyle(imgElement, 'display', 'none');
    }
  }

  private toggleControls(hasFile: boolean) {
    if (this.removeBtn && this.uploadBtn && this.overlay) {
      if (hasFile) {
        this.renderer.setStyle(this.removeBtn, 'display', 'block');
        this.renderer.setStyle(this.uploadBtn, 'display', 'none');

        if (this.placeholderOverlay) {
          this.renderer.setStyle(this.placeholderOverlay, 'display', 'none');
        }
      } else {
        this.renderer.setStyle(this.removeBtn, 'display', 'none');
        this.renderer.setStyle(this.uploadBtn, 'display', 'block');

        if (this.placeholderOverlay) {
          this.renderer.setStyle(this.placeholderOverlay, 'display', 'flex');
        }
      }
    }
  }

  private openFileDialog() {
    this.fileInput?.click();
  }

  private removeImage() {
    this.fileChanged.emit(null);
  }

  private cleanup() {
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
    }

    if (this.fileInput) {
      this.renderer.removeChild(document.body, this.fileInput);
    }
    if (this.overlay) {
      this.renderer.removeChild(this.el.nativeElement, this.overlay);
    }
    if (this.placeholderOverlay) {
      this.renderer.removeChild(this.el.nativeElement, this.placeholderOverlay);
    }
  }
}
